import { requestTypeOf } from '@paradigm/permissions/constants/formA';

/**
 * Verb icon variant (CSS class suffix) for compact icon design.
 */
const VERB_ICON_VARIANT = {
  read: 'read',            // blue
  create: 'create',        // green
  edit: 'edit',            // amber
  delete: 'delete',        // red
  propose: 'propose',      // teal/green
  see: 'see',              // purple
  // The one that publishes. It MUST have its own entry: the fallback below is
  // 'read', so without this the most consequential permission on the card
  // rendered in the blue read colour — the gentlest mark available — while
  // actually granting the power to put someone's data in front of an audience
  // they cannot see.
  publishes: 'publishes',  // deep amber-red
};

/**
 * Strip `raw:` prefix (most common layer, just noise in display)
 * but keep `golden:`, `self:`, `context:` as they carry meaning.
 */
function schemaDisplayName(schemaName) {
  if (!schemaName) return null;
  return schemaName.replace(/^raw:/, '');
}

/**
 * Categorize DLR items into groups for the consent view:
 *   data         — node schemas with verb icons + depth hints
 *   capabilities — plugins, activity, discovery
 *
 * @param {Array} items - dlrAllItems (decorated DLR items)
 * @param {Array} schemas - full schema list for name resolution
 * @param {Object|null} effectiveGrants - schema_name → { fields, filter } for depth hints
 * @param {Object} [schemaNameMap] - optional ID→name map fallback (e.g. from entityInfo.dlr_schema_map)
 * @returns {{ data: Array, capabilities: Array }}
 */
export function categorizeDlr(items, schemas, effectiveGrants, schemaNameMap) {
  if (!items?.length) return { data: [], capabilities: [] };

  // ── Data: nodes grouped by schema ──
  const schemaMap = new Map(); // schemaName → { verbs, fields, filter, icon }

  // ── Graph: relationships ──

  // ── Capabilities ──
  const capPluginNames = [];
  const capPluginIds = [];
  // Per-tool modes the ask carries: { pluginId: { toolId: 'allow'|'ask'|'deny' } }.
  // A plugin is not a capability — Google is calendar, contacts, mail, files and
  // watch history — so an ask that narrows by tool has to SAY SO here. Without
  // this the backend enforced a restriction the consent screen never mentioned,
  // which is consent given to a shape nobody could see.
  const capToolModes = {};
  // Which plugin verbs were asked, and whether any ask named NO plugin. An ask
  // with no `plugin_ids` is the widest one — the checker lets it run any plugin
  // (authz/checker.py) — and the card used to write a line only when plugins were
  // named, so the widest ask was the one it left out.
  const capPluginVerbs = new Set();
  let capAllPlugins = false;
  let hasActivity = false;
  const activityCategories = [];
  let hasFriends = false;
  let hasGroups = false;
  let hasRealms = false;
  const autoVerbs = new Set();
  let hasSubEntity = false;

  for (const item of items) {
    const rt = requestTypeOf(item);
    if (!rt) continue;

    const parts = rt.split(':');
    const resource = parts[0];
    const verb = parts[1];
    const schemaIds = item.schema_ids || [];

    if (resource === 'nodes') {
      const hasSchemas = schemaIds.length > 0 || item.schema_name;
      if (hasSchemas) {
        // Per-schema row
        const resolved = resolveSchemaInfo(schemaIds, item.schema_name, schemas, schemaNameMap);
        for (const { name, icon } of resolved) {
          if (!schemaMap.has(name)) schemaMap.set(name, { verbs: new Set(), fields: null, filter: null, icon: null, hasWriteVerb: false, isAllNodes: false });
          const entry = schemaMap.get(name);
          if (icon && !entry.icon) entry.icon = icon;
          if (['edit', 'create', 'delete'].includes(verb)) entry.hasWriteVerb = true;
          entry.verbs.add(verb);
          if (item.fields?.length) entry.fields = item.fields;
          if (item.filter && typeof item.filter === 'object') entry.filter = item.filter;
        }
      } else {
        // Schema-less = "all nodes" — show as a visible row (not silently skipped)
        const ALL_KEY = '__all_nodes__';
        if (!schemaMap.has(ALL_KEY)) schemaMap.set(ALL_KEY, { verbs: new Set(), fields: null, filter: null, icon: null, hasWriteVerb: false, isAllNodes: true });
        const entry = schemaMap.get(ALL_KEY);
        if (['edit', 'create', 'delete'].includes(verb)) entry.hasWriteVerb = true;
        entry.verbs.add(verb);
      }
        } else if (resource === 'plugins') {
      if (verb === 'execute' || verb === 'prepare') {
        capPluginVerbs.add(verb);
        if (!item.plugin_names?.length && !item.plugin_ids?.length) capAllPlugins = true;
      }
      if (item.plugin_names?.length) {
        capPluginNames.push(...item.plugin_names.filter(Boolean));
      } else if (item.plugin_ids?.length) {
        capPluginIds.push(...item.plugin_ids.map(String));
      }
      // NARROWER WINS across items, the same rule the backend folds with
      // (authz/dlr.py and authz/access.py both rank deny < ask < allow). Two
      // request items naming one tool differently must not widen by accident,
      // and a screen that ranked them differently from the enforcer would show
      // a promise the platform does not keep.
      if (item.tools && typeof item.tools === 'object') {
        const order = { deny: 0, ask: 1, allow: 2 };
        for (const [pid, modes] of Object.entries(item.tools)) {
          if (!modes || typeof modes !== 'object') continue;
          const cur = capToolModes[String(pid)] || (capToolModes[String(pid)] = {});
          for (const [tid, mode] of Object.entries(modes)) {
            if (!(mode in order)) continue;
            const prev = cur[String(tid)];
            if (prev === undefined || order[mode] < order[prev]) cur[String(tid)] = mode;
          }
        }
      }
    } else if (resource === 'activity') {
      hasActivity = true;
      if (item.activity_categories?.length) {
        activityCategories.push(...item.activity_categories);
      }
    } else if (resource === 'discovery') {
      // Canonical items carry one verb each; the flag defaults are only for the
      // legacy flags-only item (see deriveVerbsFromRequests for the same split).
      if (item.verb) {
        if (item.verb === 'friends' || item.friends === true) hasFriends = true;
        if (item.verb === 'groups' || item.groups === true) hasGroups = true;
        if (item.verb === 'realms' || item.realms === true) hasRealms = true;
      } else {
        if (item.friends !== false) hasFriends = true;
        if (item.groups !== false) hasGroups = true;
        if (item.realms === true) hasRealms = true;
      }
    } else if (resource === 'automations') {
      if (verb) autoVerbs.add(verb);
    } else if (resource === 'sub_entity') {
      hasSubEntity = true;
    }
  }

  // ── Build data rows with depth hints ──
  const data = [];
  for (const [schema, entry] of schemaMap) {
    const verbList = Array.from(entry.verbs).map(v => ({
      verb: v,
      variant: VERB_ICON_VARIANT[v] || v,
    }));

    // Depth hints from effectiveGrants OR from DLR item fields/filter
    let fieldHint = null;
    let hasFilter = false;
    // First check DLR item-level fields (the new permission language)
    if (entry.fields?.length) {
      const schemaObj = schemas.find(s =>
        schemaDisplayName(s.schema_name) === schema
      );
      const totalFields = schemaObj?.json_schema?.properties
        ? Object.keys(schemaObj.json_schema.properties).length
        : null;
      fieldHint = {
        count: entry.fields.length,
        total: totalFields,
        fieldNames: entry.fields,
      };
    }
    let filterInfo = null;
    let filterDetail = null;
    if (entry.filter) {
      const parsed = parseFilter(entry.filter);
      filterInfo = parsed.text;
      filterDetail = parsed.rules;
    }
    // Then overlay effectiveGrants if available (user-narrowed)
    if (effectiveGrants) {
      const grant = effectiveGrants[schema];
      if (grant) {
        if (grant.fields?.length && !fieldHint) {
          const schemaObj = schemas.find(s =>
            schemaDisplayName(s.schema_name) === schema
          );
          const totalFields = schemaObj?.json_schema?.properties
            ? Object.keys(schemaObj.json_schema.properties).length
            : null;
          fieldHint = {
            count: grant.fields.length,
            total: totalFields,
          };
        }
        if (grant.filter && Object.keys(grant.filter).length > 0 && !filterInfo) {
          const parsed = parseFilter(grant.filter);
          filterInfo = parsed.text;
          filterDetail = parsed.rules;
        }
      }
    }

    const displayName = entry.isAllNodes ? 'All nodes' : schema;
    data.push({ schema: displayName, verbs: verbList, fieldHint, filterInfo, filterDetail, icon: entry.icon, hasWriteVerb: entry.hasWriteVerb, isAllNodes: entry.isAllNodes });
  }

  // ── Build capability rows ──
  const capabilities = [];

  const pluginTags = capPluginNames.length > 0
    ? capPluginNames
    : capPluginIds.length > 0
      ? capPluginIds
      : [];
  if (pluginTags.length > 0 || capPluginVerbs.size > 0) {
    // Only the NARROWINGS are surfaced. An `allow` is what "run plugins"
    // already meant, so listing it would pad the card with restatements and
    // bury the two lines that actually change what happens — and a card people
    // skim past is a card that has stopped obtaining consent.
    const toolLimits = [];
    for (const [pid, modes] of Object.entries(capToolModes)) {
      for (const [tid, mode] of Object.entries(modes)) {
        if (mode === 'ask' || mode === 'deny') {
          toolLimits.push({ pluginId: pid, toolId: tid, mode });
        }
      }
    }
    // Strongest first: a list that opens with "asks before X" and buries
    // "never Y" is a list designed to be skimmed past.
    toolLimits.sort((x, y) => (x.mode === y.mode ? 0 : x.mode === 'deny' ? -1 : 1));
    // `prepare` is not a weaker `execute`: it fills in a run for the person to
    // authorise and can cause none (formA.js), so it is not "use".
    const runs = capPluginVerbs.has('execute') || !capPluginVerbs.has('prepare');
    capabilities.push({
      kind: 'plugins',
      text: runs
        ? (capAllPlugins ? 'Use any of your tools' : 'Use tools')
        : (capAllPlugins ? 'Prepare runs of any of your tools' : 'Prepare runs of your tools'),
      detailTags: capAllPlugins ? [] : pluginTags,
      ...(capAllPlugins ? { allPlugins: true } : {}),
      ...(toolLimits.length ? { toolLimits } : {}),
    });
  }

  if (hasActivity) {
    capabilities.push({
      kind: 'activity',
      text: 'View activity log',
      detailTags: activityCategories.length > 0 ? activityCategories : [],
    });
  }

  if (hasFriends || hasGroups) {
    const parts = [];
    if (hasFriends) parts.push('friends');
    if (hasGroups) parts.push('groups');
    capabilities.push({
      kind: 'discovery',
      text: 'See your social graph',
      detailTags: parts,
    });
  }

  if (hasRealms) {
    capabilities.push({
      kind: 'discovery',
      text: 'Discover your privacy realms',
      detailTags: ['realms'],
    });
  }

  if (autoVerbs.size > 0) {
    const parts = [];
    if (autoVerbs.has('write')) parts.push('manage');
    if (autoVerbs.has('execute')) parts.push('run');
    capabilities.push({ kind: 'automations', text: 'Run automations', detailTags: parts });
  }
  if (hasSubEntity) {
    capabilities.push({ kind: 'sub_entity', text: 'Declare sub-entities', detailTags: [] });
  }

  return { data, capabilities };
}

/**
 * Categorize verb_caps (from realm ceiling or EP consent) into the same shape
 * as categorizeDlr() output: { data, capabilities }.
 *
 * This lets PermissionSummary render realm/EP data with the same visual language
 * as the consent page — same icons, same layout, same component tree.
 *
 * @param {Object} verbCaps - { nodes: ['read','edit'], relationships: ['read'], ... }
 * @param {Object} [scopeFilters] - per-verb scope (schema_ids, node_ids, etc.)
 * @param {Array} [schemas] - full schema list for name/icon resolution
 * @param {Object} [accessBlob] - optional full access blob for reading scope_type
 * @returns {{ data: Array, capabilities: Array }}
 */
export function categorizeVerbCaps(verbCaps, scopeFilters, schemas, accessBlob) {
  const vc = verbCaps || {};
  const sf = scopeFilters || {};
  const data = [];
  const capabilities = [];

  // ── Nodes ──
  const nodeVerbs = vc.nodes || [];
  if (nodeVerbs.length > 0) {
    // Check if any verb is global (no scope_filters entry) vs per-schema
    const globalVerbs = [];
    const schemaVerbMap = new Map(); // schemaId → Set<verb>

    // A verb scoped to specific NODE IDS and no schemas matched neither branch
    // below and silently produced no row — so a realm granting read of two named
    // nodes summarised itself as "No permissions". On a privacy surface that is
    // not a missing row, it is the opposite of the truth, stated on the page
    // somebody opens to check what they have exposed.
    const nodeIdVerbs = new Map();   // verb → count of node ids

    for (const verb of nodeVerbs) {
      const scope = sf[`nodes:${verb}`];
      if (!scope || (!scope.schema_ids?.length && !scope.node_ids?.length)) {
        globalVerbs.push(verb);
        continue;
      }
      if (scope.schema_ids?.length) {
        for (const sid of scope.schema_ids) {
          if (!schemaVerbMap.has(sid)) schemaVerbMap.set(sid, new Set());
          schemaVerbMap.get(sid).add(verb);
        }
      }
      // Not `else` — a verb can be scoped to schemas AND to individual nodes,
      // and reporting only the schemas would understate it.
      if (scope.node_ids?.length) {
        nodeIdVerbs.set(verb, scope.node_ids.length);
      }
    }

    // Read scope_type from the access blob for display (user's consent choice)
    const blobNodes = (accessBlob || {}).nodes || {};
    // The own/any choice now lives on `edit` alone; `modify` no longer exists
    // to carry it.
    const writeScopeType = blobNodes.edit?.scope_type || null;

    // Global verbs → "All nodes" row
    if (globalVerbs.length > 0) {
      data.push({
        schema: 'All nodes',
        verbs: globalVerbs.map(v => ({ verb: v, variant: VERB_ICON_VARIANT[v] || v })),
        isAllNodes: true,
        icon: null,
        fieldHint: null,
        filterInfo: null,
        filterDetail: null,
        isOwn: writeScopeType === 'own',
      });
    }

    // Per-schema verbs → individual rows
    for (const [sid, verbs] of schemaVerbMap) {
      const s = (schemas || []).find(sch => String(sch.id) === String(sid));
      const name = s ? schemaDisplayName(s.schema_name) : `schema:${sid}`;
      const icon = s?.schema_metadata?.icon || null;
      data.push({
        schema: name,
        verbs: Array.from(verbs).map(v => ({ verb: v, variant: VERB_ICON_VARIANT[v] || v })),
        isAllNodes: false,
        icon,
        fieldHint: null,
        filterInfo: null,
        filterDetail: null,
        isOwn: writeScopeType === 'own',
      });
    }

    // Individually chosen nodes. One row per distinct count so "read 2 nodes,
    // edit 1" cannot collapse into a single misleading line.
    const byCount = new Map();       // count → Set<verb>
    for (const [verb, count] of nodeIdVerbs) {
      if (!byCount.has(count)) byCount.set(count, new Set());
      byCount.get(count).add(verb);
    }
    for (const [count, verbs] of byCount) {
      data.push({
        schema: `${count} specific node${count === 1 ? '' : 's'}`,
        verbs: Array.from(verbs).map(v => ({ verb: v, variant: VERB_ICON_VARIANT[v] || v })),
        isAllNodes: false,
        isSpecificNodes: true,
        icon: null,
        fieldHint: null,
        filterInfo: null,
        filterDetail: null,
        isOwn: writeScopeType === 'own',
      });
    }
  }

  // ── Capabilities ──
  const pluginVerbs = vc.plugins || [];
  if (pluginVerbs.includes('execute')) {
    capabilities.push({ kind: 'plugins', text: 'Use tools', detailTags: [] });
  }

  const actVerbs = vc.activity || [];
  if (actVerbs.includes('read')) {
    const cats = sf['activity:read']?.categories || [];
    capabilities.push({ kind: 'activity', text: 'View activity log', detailTags: cats });
  }

  const discVerbs = vc.discovery || [];
  const socialDisc = discVerbs.filter(v => v !== 'realms');
  if (socialDisc.length > 0) {
    capabilities.push({ kind: 'discovery', text: 'See your social graph', detailTags: socialDisc });
  }
  if (discVerbs.includes('realms')) {
    capabilities.push({ kind: 'discovery', text: 'Discover your privacy realms', detailTags: ['realms'] });
  }

  const autoVerbsC = vc.automations || [];
  if (autoVerbsC.length > 0) {
    const parts = [];
    if (autoVerbsC.includes('write')) parts.push('manage');
    if (autoVerbsC.includes('execute')) parts.push('run');
    capabilities.push({ kind: 'automations', text: 'Run automations', detailTags: parts });
  }
  const subEntVerbsC = vc.sub_entity || [];
  if (subEntVerbsC.length > 0) {
    capabilities.push({ kind: 'sub_entity', text: 'Declare sub-entities', detailTags: [] });
  }

  return { data, capabilities };
}

/**
 * Resolve schema IDs or name to display names + icons.
 * @param {string[]} schemaIds
 * @param {string|null} schemaName - fallback comma-joined name
 * @param {Array} schemas - full schema list for lookup
 * @param {Object} idMap - optional ID→name map (e.g. from entityInfo.dlr_schema_map)
 * @returns {{ name: string, icon: string|null }[]}
 */
function resolveSchemaInfo(schemaIds, schemaName, schemas, idMap) {
  const results = [];
  if (schemaIds.length > 0) {
    for (const id of schemaIds) {
      const s = schemas.find(sch => String(sch.id) === String(id));
      const resolved = s?.schema_name || (idMap && idMap[String(id)]) || null;
      const icon = s?.schema_metadata?.icon || null;
      results.push({
        name: resolved ? schemaDisplayName(resolved) : `schema:${id}`,
        icon,
      });
    }
  } else if (schemaName) {
    results.push({ name: schemaDisplayName(schemaName), icon: null });
  }
  return results;
}

/**
 * Parse a filter object into structured rules for rendering.
 * @param {Object} filter - e.g. { metric_type: { in: ["sleep_quality", "mood"] } }
 * @returns {{ text: string, rules: { field: string, op: string, values: string[] }[] }}
 */
function parseFilter(filter) {
  if (!filter || typeof filter !== 'object') return { text: null, rules: [] };
  const rules = [];
  for (const [field, condition] of Object.entries(filter)) {
    if (condition && typeof condition === 'object') {
      if (condition.in && Array.isArray(condition.in)) {
        rules.push({ field, op: 'is', values: condition.in.map(String) });
      } else if (condition.eq !== undefined) {
        rules.push({ field, op: 'is', values: [String(condition.eq)] });
      } else if (condition.neq !== undefined) {
        rules.push({ field, op: 'is not', values: [String(condition.neq)] });
      } else if (condition.gt !== undefined) {
        rules.push({ field, op: '>', values: [String(condition.gt)] });
      } else if (condition.lt !== undefined) {
        rules.push({ field, op: '<', values: [String(condition.lt)] });
      } else {
        rules.push({ field, op: 'restricted', values: [] });
      }
    }
  }
  const textParts = rules.map(r =>
    r.values.length > 0 ? `${r.field} ${r.op} ${r.values.join(' or ')}` : `${r.field} ${r.op}`
  );
  return {
    text: textParts.length > 0 ? `only where ${textParts.join(', ')}` : null,
    rules,
  };
}
